type GridLayoutItem = {
    i: string | number;
    h?: number;
    minH?: number;
    minW?: number;
    w?: number;
    x?: number;
    y?: number;
    moved?: boolean;
    static?: boolean;
    [key: string]: unknown;
};

type NormalizeGridLayoutArgs = {
    layout: GridLayoutItem[];
    measuredHeights: Record<string, number>;
    getMinRowsForWidget: (widgetId: string) => number;
    gridCols: number;
    minW?: number;
};

const getSafeX = (x: number | undefined, maxX: number): number =>
    typeof x === 'number' && Number.isFinite(x) ? Math.min(maxX, Math.max(0, x)) : 0;

const getSafeY = (y: number | undefined, fallbackY: number, maxReasonableY: number): number =>
    typeof y === 'number' && Number.isFinite(y) && y >= 0 && y <= maxReasonableY ? y : fallbackY;

export const normalizeWidgetGridLayout = ({
    layout,
    measuredHeights,
    getMinRowsForWidget,
    gridCols,
    minW = 14,
}: NormalizeGridLayoutArgs): GridLayoutItem[] => {
    if (!layout.length) return [];

    const widgetCount = layout.length;

    const normalizedLayout = layout.map((layoutItem: GridLayoutItem, index: number) => {
        const widgetId = layoutItem.i;
        const widgetIdKey = String(widgetId ?? '');
        const measuredH = measuredHeights[widgetIdKey];
        const minRows = getMinRowsForWidget(widgetIdKey);
        const normalizedH = Math.max(minRows, layoutItem.h ?? minRows);
        const newH = measuredH ? Math.max(minRows, measuredH) : normalizedH;
        const nextMinH = Math.max(minRows, Math.min(layoutItem.minH ?? newH, newH));
        const safeW = Math.min(gridCols, Math.max(minW, layoutItem.w ?? minW));
        const maxX = Math.max(0, gridCols - safeW);
        const safeX = getSafeX(layoutItem.x, maxX);
        const fallbackY = index * (newH + 1);
        const maxReasonableY = Math.max(fallbackY + 20, widgetCount * 12);
        const safeY = getSafeY(layoutItem.y, fallbackY, maxReasonableY);

        return {
            ...layoutItem,
            i: widgetId,
            x: safeX,
            y: safeY,
            w: safeW,
            h: newH,
            minW,
            minH: nextMinH,
        };
    });

    const minY = normalizedLayout.reduce(
        (currentMin: number, item: GridLayoutItem) => Math.min(currentMin, item.y ?? 0),
        Number.POSITIVE_INFINITY
    );

    if (Number.isFinite(minY) && minY > 0) {
        return normalizedLayout.map((item: GridLayoutItem) => ({
            ...item,
            y: Math.max(0, (item.y ?? 0) - minY),
        }));
    }

    return normalizedLayout;
};
